import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface InjectionTest {
  id: string;
  device_tag: string;
  device_type: string;
  substation_name: string;
  test_date: string;
  next_test_due: string | null;
  test_result: string;
  test_company: string;
  settings_verified: boolean;
  tester_name: string;
}

const resultColor = (result: string) => {
  switch (result?.toLowerCase()) {
    case 'pass': return '#4caf50';
    case 'fail': return '#f44336';
    case 'conditional': return '#ff9800';
    default: return '#888';
  }
};

const TestingLibrary: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [tests, setTests] = useState<InjectionTest[]>([]);
  const [siteName, setSiteName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!siteId) return;
    const load = async () => {
      try {
        const [siteRes, testsRes] = await Promise.all([
          api.get(`/api/sites/${siteId}`),
          api.get(`/api/sites/${siteId}/injection-tests`),
        ]);
        setSiteName(siteRes.data.name);
        setTests(testsRes.data || []);
      } catch {
        setError('Failed to load injection tests.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [siteId]);

  const overdueCount = tests.filter(
    (t) => t.next_test_due && t.next_test_due < today
  ).length;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f6fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <header style={{
        background: '#1a1a2e',
        color: '#fff',
        padding: '0 32px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate(`/site/${siteId}`)}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            padding: '7px 16px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'inherit',
          }}
        >
          ← Site Overview
        </button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{siteName || 'Protection Testing Library'}</div>
          <div style={{ fontSize: 11, color: '#8899aa', letterSpacing: 1 }}>🧪 INJECTION TEST RECORDS</div>
        </div>
      </header>

      <main style={{ flex: 1, padding: 32 }}>
        {loading && <div style={{ textAlign: 'center', color: '#888', padding: 60 }}>Loading tests...</div>}
        {error && (
          <div style={{
            background: '#fff5f5', border: '1px solid #f44336', borderRadius: 8,
            color: '#f44336', padding: '14px 18px', marginBottom: 24,
          }}>
            {error}
          </div>
        )}

        {!loading && overdueCount > 0 && (
          <div style={{
            background: '#fff3e0',
            border: '1px solid #ff9800',
            borderLeft: '4px solid #ff9800',
            borderRadius: 8,
            padding: '12px 20px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: '#7a4e00',
            fontSize: 14,
          }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <strong>{overdueCount} device{overdueCount !== 1 ? 's' : ''} overdue for injection testing</strong>
          </div>
        )}

        {!loading && (
          <div style={{
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
            overflow: 'hidden',
          }}>
            {tests.length === 0 ? (
              <div style={{ color: '#aaa', fontSize: 14, padding: 32 }}>No injection tests found for this site.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#0f3460', color: '#fff' }}>
                    {['Device Tag', 'Type', 'Substation', 'Test Date', 'Next Due', 'Result', 'Company', 'Settings Verified'].map((h) => (
                      <th key={h} style={{
                        padding: '11px 14px',
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: 12,
                        letterSpacing: 0.5,
                        whiteSpace: 'nowrap',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tests.map((t, i) => {
                    const isOverdue = !!(t.next_test_due && t.next_test_due < today);
                    return (
                      <tr key={t.id} style={{
                        background: isOverdue
                          ? '#fff8e1'
                          : i % 2 === 0 ? '#fff' : '#f8f9fc',
                      }}>
                        <td style={{ padding: '11px 14px', fontWeight: 700, color: '#0f3460' }}>
                          {t.device_tag}
                          {isOverdue && <span style={{ marginLeft: 6, fontSize: 10, color: '#ff6f00', fontWeight: 600 }}>OVERDUE</span>}
                        </td>
                        <td style={{ padding: '11px 14px', color: '#555' }}>{t.device_type ?? '—'}</td>
                        <td style={{ padding: '11px 14px', color: '#555' }}>{t.substation_name ?? '—'}</td>
                        <td style={{ padding: '11px 14px', color: '#555', whiteSpace: 'nowrap' }}>{t.test_date ?? '—'}</td>
                        <td style={{
                          padding: '11px 14px',
                          whiteSpace: 'nowrap',
                          color: isOverdue ? '#d84315' : '#555',
                          fontWeight: isOverdue ? 600 : 400,
                        }}>
                          {t.next_test_due ?? '—'}
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          {t.test_result ? (
                            <span style={{
                              background: resultColor(t.test_result) + '22',
                              color: resultColor(t.test_result),
                              border: `1px solid ${resultColor(t.test_result)}44`,
                              borderRadius: 20,
                              padding: '3px 10px',
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: 'capitalize',
                            }}>
                              {t.test_result}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '11px 14px', color: '#555' }}>{t.test_company ?? '—'}</td>
                        <td style={{ padding: '11px 14px' }}>
                          <span style={{
                            background: t.settings_verified ? '#4caf5022' : '#f4433622',
                            color: t.settings_verified ? '#4caf50' : '#f44336',
                            border: `1px solid ${t.settings_verified ? '#4caf5044' : '#f4433644'}`,
                            borderRadius: 20,
                            padding: '3px 10px',
                            fontSize: 11,
                            fontWeight: 600,
                          }}>
                            {t.settings_verified ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>

      <footer style={{
        background: '#1a1a2e',
        color: '#8899aa',
        textAlign: 'center',
        padding: '14px',
        fontSize: 12,
        flexShrink: 0,
      }}>
        © 2026 JMB Engineering Pty Ltd — Protection Platform
      </footer>
    </div>
  );
};

export default TestingLibrary;
